import { colors } from 'Util/colors'
import { TOOLBAR_HEIGHT } from 'Util/constants'
import { fiFI } from '@mui/material/locale'
import { createTheme } from '@mui/material/styles'
import type { SimplePaletteColorOptions } from '@mui/material/styles/createPalette'

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: SimplePaletteColorOptions
    midnight: SimplePaletteColorOptions
    focus: SimplePaletteColorOptions
    successSelected: SimplePaletteColorOptions
  }
  interface PaletteOptions {
    tertiary: SimplePaletteColorOptions
    midnight: SimplePaletteColorOptions
    focus: SimplePaletteColorOptions
    successSelected: SimplePaletteColorOptions
  }
}

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    learn: true
  }
  interface AlertClasses {
    standardLearn: string
    outlinedLearn: string
    filledLearn: string
    standardInfo: string
    outlinedInfo: string
    filledInfo: string
  }
}

const defaultTheme = createTheme()

// Alert styles for light mode
const lightModeAlertStyles = {
  standardLearn: {
    color: colors.learn[900],
    backgroundColor: colors.learn[50],
    '& .MuiAlert-icon': {
      color: colors.learn[800]
    }
  },
  outlinedLearn: {
    color: colors.learn[900],
    border: `1px solid ${colors.learn[500]}`,
    backgroundColor: 'transparent',
    '& .MuiAlert-icon': {
      color: colors.learn[800]
    }
  },
  filledLearn: {
    color: colors.learn[900],
    backgroundColor: colors.learn[50],
    '& .MuiAlert-icon': {
      color: colors.learn[800]
    }
  },
  standardInfo: {
    color: colors.info[900],
    backgroundColor: colors.info[50],
    '& .MuiAlert-icon': {
      color: colors.info[400]
    }
  },
  outlinedInfo: {
    color: colors.info[900],
    border: `1px solid ${colors.info[300]}`,
    backgroundColor: 'transparent',
    '& .MuiAlert-icon': {
      color: colors.info[800]
    }
  },
  filledInfo: {
    color: colors.info[900],
    backgroundColor: colors.info[50],
    '& .MuiAlert-icon': {
      color: colors.info[400]
    }
  }
}

// Alert styles for dark mode
const darkModeAlertStyles = {
  standardLearn: {
    color: colors.learn[50],
    backgroundColor: colors.learn[900],
    '& .MuiAlert-icon': {
      color: colors.learn[400]
    }
  },
  outlinedLearn: {
    color: colors.learn[50],
    border: `1px solid ${colors.learn[300]}`,
    backgroundColor: 'transparent',
    '& .MuiAlert-icon': {
      color: colors.learn[400]
    }
  },
  filledLearn: {
    color: colors.learn[50],
    backgroundColor: colors.learn[900],
    '& .MuiAlert-icon': {
      color: colors.learn[400]
    }
  },
  standardInfo: {
    color: colors.info[50],
    backgroundColor: colors.info[900],
    '& .MuiAlert-icon': {
      color: colors.info[400]
    }
  },
  outlinedInfo: {
    color: colors.info[200],
    border: `1px solid ${colors.info[700]}`,
    backgroundColor: 'transparent',
    '& .MuiAlert-icon': {
      color: colors.info[400]
    }
  },
  filledInfo: {
    color: colors.info[50],
    backgroundColor: colors.info[900],
    '& .MuiAlert-icon': {
      color: colors.info[400]
    }
  }
}

// Helper function to get theme-aware styles
const getAlertStyles =
  (variant: keyof typeof lightModeAlertStyles) =>
  ({ theme }: { theme: { palette: { mode: string } } }) =>
    theme.palette.mode === 'dark' ? darkModeAlertStyles[variant] : lightModeAlertStyles[variant]

const Theme = createTheme(
  {
    palette: {
      mode: 'light', // Default mode
      primary: {
        main: colors.walttiViolet[500],
        light: colors.walttiViolet[300]
      },
      secondary: {
        main: colors.walttiBrightViolet[400]
      },
      text: {
        primary: colors.textPrimaryLight,
        secondary: colors.textSecondaryLight
      },
      background: {
        default: colors.grey[0],
        paper: colors.grey[50]
      },
      error: {
        main: colors.error[500]
      },
      warning: {
        main: colors.warning[500]
      },
      success: {
        main: colors.success[500]
      },
      info: {
        main: colors.info[500]
      },
      tertiary: {
        main: colors.walttiOrange[700]
      },
      midnight: {
        main: colors.walttiMidnight[700]
      },
      focus: {
        main: 'rgba(140, 125, 250, 0.12)'
      },
      successSelected: {
        main: 'rgba(76, 175, 80, 0.16)'
      }
    },
    colorSchemes: {
      dark: {
        palette: {
          mode: 'dark',
          primary: {
            main: colors.walttiBrightViolet[400]
          },
          secondary: {
            main: colors.walttiViolet[500]
          },
          text: {
            primary: colors.textPrimaryDark,
            secondary: colors.textSecondaryDark
          },
          background: {
            default: colors.walttiMidnight[700],
            paper: colors.walttiMidnight[600]
          },
          tertiary: {
            main: colors.walttiOrange[700]
          },
          midnight: {
            main: colors.walttiMidnight[700]
          },
          focus: {
            main: 'rgba(140, 125, 250, 0.12)'
          },
          successSelected: {
            main: 'rgba(76, 175, 80, 0.16)'
          }
        }
      }
    },
    typography: {
      fontFamily: 'Nunito Sans',
      h1: {
        fontSize: 26,
        fontFamily: 'Prompt',
        fontDisplay: 'swap'
      },
      h2: {
        fontSize: 22,
        fontFamily: 'Prompt',
        fontDisplay: 'swap'
      },
      h3: {
        fontSize: 18,
        fontFamily: 'Prompt',
        fontDisplay: 'swap'
      },
      h6: {
        fontSize: 16
      },
      button: {
        textTransform: 'none'
      }
    },
    components: {
      MuiToolbar: {
        styleOverrides: {
          regular: {
            minHeight: TOOLBAR_HEIGHT
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: '30px',
            whiteSpace: 'nowrap',
            boxShadow: 'none',
            '&.MuiButton-sizeSmall': {
              lineHeight: 1.7,
              padding: defaultTheme.spacing(0.5, 2)
            },
            '&.MuiButton-sizeMedium': {
              lineHeight: 1.7,
              padding: defaultTheme.spacing(0.75, 2)
            },
            '.MuiSvgIcon-root': {
              fontWeight: 600
            }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: colors.walttiViolet[500]
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 600
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: 'none'
          }
        }
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderTop: '1px solid rgba(0, 0, 0, 0.12)'
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          standardLearn: getAlertStyles('standardLearn'),
          outlinedLearn: getAlertStyles('outlinedLearn'),
          filledLearn: getAlertStyles('filledLearn'),
          standardInfo: getAlertStyles('standardInfo'),
          outlinedInfo: getAlertStyles('outlinedInfo'),
          filledInfo: getAlertStyles('filledInfo')
        }
      }
    }
  },
  fiFI
)

export default Theme
